<template>
  <v-container>
    <v-row id="cwes">
      <v-col cols="12">
        <h4 class="overline mb-1 d-flex">Common Weakness Enumeration&trade;</h4>
        <v-card id="cweTable" flat color="bg_darkerish">
          <v-card-title class="font-weight-light pt-0 pb-2">
            <v-row no-gutters>
              <v-col cols="12" order="1" sm="4" order-sm="1" class="px-2">
                <v-select
                  v-model="playFilters"
                  :items="playOptions"
                  @change="searchAndFilter"
                  :disabled="noMappedPlays"
                  multiple
                  label="Filter by Play"
                  color="dw_green"
                  item-color="dw_green"
                  item-text="id"
                  return-object
                  single-line
                  hide-details
                  clearable
                  :clear-icon="mdiClose"
                  class="text-caption"
                >
                </v-select>
              </v-col>
              <v-col cols="12" order="3" sm="4" order-sm="2" class="px-2">
                <v-select
                  v-model="statusFilter"
                  :items="statusOptions"
                  @change="searchAndFilter"
                  label="Filter by CWE Status"
                  color="dw_green"
                  item-color="dw_green"
                  item-text="name"
                  return-object
                  single-line
                  hide-details
                  clearable
                  :clear-icon="mdiClose"
                  class="text-caption"
                ></v-select>
              </v-col>
              <v-col cols="12" order="4" sm="4" order-sm="3" class="px-2">
                <v-text-field
                  v-model="search"
                  @keydown.enter="searchAndFilter"
                  label="Search"
                  color="dw_green"
                  clearable
                  @click:clear="() => search === null ? searchAndFilter : null"
                  :clear-icon="mdiClose"
                  single-line
                  hide-details
                  class="text-caption">
                  <template v-slot:append>
                    <v-icon small class="ml-1" v-text="mdiLayersSearchOutline" />
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" order="2" sm="4" order-sm="4" class="px-2">
                <v-switch
                  v-model="noMappedPlays"
                  label="No Mapped Plays"
                  @change="searchAndFilter"
                  color="dw_green"
                  dense
                  hide-details
                ></v-switch>
              </v-col>
            </v-row>
          </v-card-title>

          <v-data-table
            :headers="headers"
            :items="cwes"
            :items-per-page.sync="itemsPerPage"
            :page.sync="pageNum"
            :single-select="true"
            @click:row="(rowItem, { select }) => select(rowItem)"
            @item-selected="({ item }) =>
              $router.push({ name: 'cwe_view', params: { id: item.id }})"
            item-key="id"
            show-select
            class="elevation-1 bg_darkerish"
            color="dw_green"
            hide-default-footer
          >
            <template v-slot:no-data>
              <div class="font-weight-light text-end">No CWEs found</div>
            </template>
            <template v-slot:item.data-table-select="{ isSelected, select }">
              <div @click="select">
                <v-icon v-if="isSelected" small color="dw_green" v-text="mdiRecordCircleOutline" />
                <v-icon v-else small color="white" v-text="mdiCircleOutline" />
              </div>
            </template>
            <template v-slot:item.id="{ value }">
              <div class="font-weight-light">CWE-{{ value }}</div>
            </template>
            <template v-slot:item.name="{ value }">
              <div class="text-capitalize font-weight-light">{{ value.toLowerCase() }}</div>
            </template>
            <template v-slot:item.weakness_abstraction="{ value }">
              <div class="font-weight-light">{{ value }}</div>
            </template>

            <template v-slot:footer>
              <v-divider></v-divider>
              <v-row class="caption pa-2" no-gutters align="center" justify="center">
                <v-col cols="4" class="d-flex ml-2">
                  <v-row no-gutters align-content="start">
                    <v-col  cols="6" align-self="center">
                      <span class="grey--text">Rows per page:</span>
                    </v-col>
                    <v-col cols="4">
                      <v-select
                        dense
                        v-model="itemsPerPage"
                        :items="perPage"
                        item-text="text"
                        item-value="value"
                        color="dw_green"
                        item-color="dw_green"
                        single-line
                        hide-details
                        title="Rows per Page"
                        class="ml-2 pb-1 pl-1 my-auto caption"
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-col>
                <v-spacer></v-spacer>
                <span class="mx-4 grey--text">
                  {{ pageStart + 1 }}-{{ pageStop }} of {{ cweCount }}
                </span>
                <v-btn
                  fab
                  x-small
                  text
                  class="mr-1"
                  title="Previous Page"
                  :disabled="prevPageDisabled"
                  @click="goToPrevPage">
                  <v-icon small v-text="mdiChevronLeft" />
                </v-btn>
                <v-btn
                  fab
                  x-small
                  text
                  class="mr-1"
                  title="Next Page"
                  :disabled="nextPageDisabled"
                  @click="goToNextPage">
                  <v-icon small v-text="mdiChevronRight" />
                </v-btn>
              </v-row>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <router-view></router-view>
    <v-row class="caption px-2 py-1" no-gutters justify="start">
      <p class="caption font-weight-light grey--text mb-0">
        &copy; 2020 The MITRE Corporation. This work is reproduced and distributed with
        the permission of The MITRE Corporation.
      </p>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {
  mdiLayersSearchOutline,
  mdiCircleOutline,
  mdiRecordCircleOutline,
  mdiClose,
  mdiCheckboxBlankOutline,
  mdiCheckboxMarked,
  mdiChevronLeft,
  mdiChevronRight
} from '@mdi/js';

export default {
  name: 'cwePanel',
  data() {
    return {
      mdiLayersSearchOutline,
      mdiCircleOutline,
      mdiRecordCircleOutline,
      mdiClose,
      mdiCheckboxBlankOutline,
      mdiCheckboxMarked,
      mdiChevronLeft,
      mdiChevronRight,
      search: '',
      playFilters: [],
      noMappedPlays: false,
      statusFilter: '',
      statusOptions: ['Draft', 'Incomplete', 'Stable', 'Deprecated'],
      headers: [
        {
          text: 'CWE ID',
          value: 'id',
          align: 'start',
          width: '10vw',
          sortable: false,
          class: 'font-weight-regular'
        },
        {
          text: 'Name',
          value: 'name',
          align: 'start',
          sortable: false,
          class: 'font-weight-regular'
        },
        {
          text: 'Weakness Abstraction',
          value: 'weakness_abstraction',
          align: 'start',
          sortable: false,
          class: 'font-weight-regular'
        }
      ],
      itemsPerPage: 5,
      perPage: [
        { text: '5', value: 5 },
        { text: '25', value: 25 }
      ],
      pageNum: 1,
      numTablePages: 1,
      pageStart: 0,
      pageStop: 5,
      cweCount: 0,
      queryPrevPage: null,
      queryNextPage: null,
      queryPrevOffset: 0,
      queryCurrentOffset: 0,
      queryNextOffset: 25,
      prevPageDisabled: true,
      nextPageDisabled: false
    };
  },
  watch: {
    search() {
      if (this.search === null) {
        this.search = '';
        this.searchAndFilter();
      }
    },
    noMappedPlays() {
      this.playFilters = [];
    },
    cwePagination() {
      this.handlePagination();
    },
    itemsPerPage() {
      this.pageNum = 1;
      this.handlePagination();
    }
  },
  computed: {
    ...mapGetters([
      'cwes',
      'cwePagination',
      'playOptions'
    ])
  },
  mounted() {
    if (this.cwePagination.count) this.handlePagination();
  },
  methods: {
    ...mapActions([
      'fetchCWEs'
    ]),
    async searchAndFilter() {
      let endpoint = '?ordering=id';
      endpoint += this.search.length > 1 ? `&search=${this.search}` : '';
      if (this.noMappedPlays) endpoint += '&no_plays=true';
      this.playFilters.forEach((play) => { endpoint += `&plays__id=${play.id}`; });
      if (this.statusFilter) endpoint += `&status=${this.statusFilter}`;
      await this.fetchCWEs(endpoint);
    },
    handlePagination() {
      this.cweCount = this.cwePagination.count;
      this.queryPrevPage = this.cwePagination.previous;
      this.queryNextPage = this.cwePagination.next;
      this.numTablePages = Math.ceil(this.cwes.length / this.itemsPerPage);

      if (this.queryPrevPage !== null) {
        this.queryPrevPage = new URL(this.queryPrevPage);
        const offset = this.queryPrevPage.searchParams.get('offset');
        this.queryPrevOffset = offset === null ? 0 : parseInt(offset, 10);
      }
      if (this.queryNextPage !== null) {
        this.queryNextPage = new URL(this.queryNextPage);
        const offset = this.queryNextPage.searchParams.get('offset');
        this.queryNextOffset = offset === null ? 0 : parseInt(offset, 10);
      }

      if (this.queryPrevPage === null) {
        this.queryCurrentOffset = 0;
      } else if (this.queryNextPage === null) {
        this.queryCurrentOffset = this.queryNextOffset;
      } else {
        this.queryCurrentOffset = (this.queryPrevOffset + this.queryNextOffset) / 2;
      }

      this.pageStart = this.queryCurrentOffset;
      let nextPageStop = this.queryCurrentOffset + this.itemsPerPage;
      nextPageStop = nextPageStop > this.cweCount ? this.cweCount : nextPageStop;
      this.pageStop = nextPageStop;
      this.prevPageDisabled = this.pageStart === 0;
      this.nextPageDisabled = this.pageStop === this.cweCount;
    },
    async goToNextPage() {
      if (this.pageNum === (this.cwes.length / this.itemsPerPage)) {
        await this.fetchCWEs(this.queryNextPage.search);
        let nextPageStop = this.queryCurrentOffset + this.itemsPerPage;
        nextPageStop = nextPageStop > this.cweCount ? this.cweCount : nextPageStop;
        this.pageNum = 1;
        this.pageStart = this.queryCurrentOffset;
        this.pageStop = nextPageStop;
      } else {
        this.pageNum += 1;
        this.pageStart += this.itemsPerPage;
        this.pageStop += this.itemsPerPage;
        if (this.pageStop > this.cweCount) this.pageStop = this.cweCount;
      }
      this.setDisabled();
    },
    async goToPrevPage() {
      if (this.pageNum === 1 && this.queryNextOffset > 0) {
        await this.fetchCWEs(this.queryPrevPage.search);
        this.pageNum = this.numTablePages;
        this.pageStart = this.queryNextOffset - this.itemsPerPage;
        this.pageStop = this.queryNextOffset;
      } else {
        this.pageNum -= 1;
        this.pageStart -= this.itemsPerPage;
        if (this.pageStop === this.cweCount) {
          this.pageStop = this.pageStart + this.itemsPerPage;
        } else this.pageStop -= this.itemsPerPage;
      }
      this.setDisabled();
    },
    setDisabled() {
      if (this.queryNextPage === null) {
        if (this.pageStop === this.cweCount) this.nextPageDisabled = true;
        else this.nextPageDisabled = false;
      } else this.nextPageDisabled = false;
      if (this.queryPrevPage === null) {
        if (this.pageStart === 0) this.prevPageDisabled = true;
        else this.prevPageDisabled = false;
      } else this.prevPageDisabled = false;
    }
  }
};
</script>
